/**
 * Dashboard — Command Center
 *
 * Design principles (Jul 2026):
 *  - Priority Zone: items that require IMMEDIATE user attention sit at the top
 *    (anomalies pending review, analysis awaiting HITL sign-off, tamper alerts)
 *  - Operational Pulse: real-time cross-vertical mission/fleet status
 *  - Vertical Activity: per-vertical KPI strip for active verticals only
 *  - Quick Actions: contextual shortcuts at the bottom
 *
 * All tiles are wired to real backend endpoints; graceful zeros on failure.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCanonical } from '../../hooks/useCanonical';
import { useAuthStore } from '../../store/authStore';
import {
  missionsApi,
  anomalyApi,
  satelliteBoundariesApi,
} from '../../services/api';
import {
  integrityApi,
  hitlApi,
  notificationsApi,
} from '../../services/enterpriseApi';
import Card from '../../components/common/Card';
import { LoadingScreen } from '../../components/common/Spinner';
import {
  AlertTriangle,
  ClipboardCheck,
  Shield,
  Navigation,
  Map,
  Image,
  Brain,
  Bell,
  ArrowRight,
  Leaf,
  Flame,
  Building2,
  TreePine,
  CheckCircle2,
  Clock,
  Activity,
  Satellite,
  ShieldAlert,
  MessageSquare,
  TrendingUp,
  BarChart3,
  Route,
  RefreshCw,
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns a JSX subtitle for a mission row.
 * Priority chain (all from fields that actually exist in the DB):
 *   1. metadata.anomaly_type + metadata.payload   — Survey Planner missions
 *   2. mission_type label                          — any typed mission
 *   3. description first sentence                 — Create Mission form
 *   4. drone_serial_number                        — if only that is set
 *   5. nothing (empty fragment)
 *
 * Notably: `location` is a PostGIS geography column, not a displayable string.
 * `metadata.location_name` and a top-level `location_name` column do not exist
 * in the schema, so we never try to read them.
 */
function missionSubtitle(m: any): React.ReactNode {
  const meta = m.metadata ?? {};

  // Survey Planner populates these
  if (meta.anomaly_type || meta.payload) {
    const parts: string[] = [];
    if (meta.anomaly_type) parts.push((meta.anomaly_type as string).replace(/_/g, ' '));
    if (meta.payload) parts.push(meta.payload as string);
    return (
      <span className="flex items-center gap-1">
        <Activity className="w-3 h-3 shrink-0" />
        {parts.join(' · ')}
      </span>
    );
  }

  // mission_type e.g. "drone_survey", "remediation"
  if (m.mission_type) {
    return (
      <span className="flex items-center gap-1">
        <Navigation className="w-3 h-3 shrink-0" />
        {(m.mission_type as string).replace(/_/g, ' ')}
      </span>
    );
  }

  // First sentence of description (Create Mission form stores free text here)
  if (m.description) {
    const first = (m.description as string).split(/[\n.]/)[0].trim();
    if (first.length > 0 && first.length < 80) {
      return <span>{first}</span>;
    }
  }

  // Drone serial / asset name
  if (m.drone_serial_number) {
    return (
      <span className="flex items-center gap-1">
        <Navigation className="w-3 h-3 shrink-0" />
        {m.drone_serial_number}
      </span>
    );
  }

  return null;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const SEVERITY_DOT: Record<string, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-amber-400',
  low: 'bg-yellow-300',
};

const MISSION_STATUS_STYLE: Record<string, string> = {
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  planned: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

// ─── Priority Attention Tile ──────────────────────────────────────────────────

interface AttentionTileProps {
  count: number;
  label: string;
  sub: string;
  Icon: React.ElementType;
  to: string;
  urgency: 'critical' | 'high' | 'medium';
  loading?: boolean;
  hasError?: boolean;
}

function AttentionTile({ count, label, sub, Icon, to, urgency, loading, hasError }: AttentionTileProps) {
  // When count is zero the tile goes silent: muted border, grey icon background.
  // Colour only fires when there is something actionable.
  // When the source query errored we show a distinct amber warning state.
  const active = !hasError && count > 0;

  const borderAccent = hasError
    ? 'border-l-4 border-amber-400 dark:border-amber-500 bg-amber-50/30 dark:bg-amber-950/10'
    : active
    ? urgency === 'critical'
      ? 'border-l-4 border-red-500 dark:border-red-500 bg-red-50/20 dark:bg-red-950/10'
      : urgency === 'high'
      ? 'border-l-4 border-orange-400 dark:border-orange-500 bg-orange-50/20 dark:bg-orange-950/10'
      : 'border-l-4 border-amber-400 dark:border-amber-400 bg-amber-50/20 dark:bg-amber-950/10'
    : 'border-l-4 border-gray-200 dark:border-gray-700';

  const iconBg = hasError
    ? 'bg-amber-400 dark:bg-amber-600'
    : active
    ? urgency === 'critical'
      ? 'bg-red-500'
      : urgency === 'high'
      ? 'bg-orange-500'
      : 'bg-amber-400'
    : 'bg-gray-200 dark:bg-gray-700';

  const iconColor = active || hasError ? 'text-white' : 'text-gray-400 dark:text-gray-500';

  const badge =
    urgency === 'critical'
      ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
      : urgency === 'high'
      ? 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
      : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';

  return (
    <Link to={to}>
      <div
        className={`relative bg-white dark:bg-metallic-900 rounded-lg shadow p-5 ${borderAccent} hover:shadow-lg transition-all h-full flex flex-col`}
      >
        {active && (
          <span
            className={`absolute -top-2 -right-2 min-w-[1.4rem] h-6 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${iconBg} text-white shadow`}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
        <div className="flex items-start gap-3">
          <div className={`${iconBg} p-2.5 rounded-lg shrink-0`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
              {label}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{sub}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          {loading ? (
            <span className="text-xs text-gray-400 animate-pulse">loading…</span>
          ) : hasError ? (
            <span className="text-xl font-bold text-amber-500 dark:text-amber-400" title="Data unavailable — API error">—</span>
          ) : (
            <span className={`text-3xl font-bold ${count === 0 ? 'text-gray-400' : 'text-gray-900 dark:text-white'}`}>
              {count}
            </span>
          )}
          <ArrowRight className="w-4 h-4 text-gray-400" />
        </div>
        {hasError ? (
          <span className="mt-2 text-xs font-medium px-2 py-0.5 rounded-full self-start bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Data unavailable
          </span>
        ) : count > 0 ? (
          <span className={`mt-2 text-xs font-medium px-2 py-0.5 rounded-full self-start ${badge}`}>
            {urgency === 'critical' ? 'Action required' : urgency === 'high' ? 'Needs review' : 'Pending'}
          </span>
        ) : null}
      </div>
    </Link>
  );
}

// ─── Stat Chip ────────────────────────────────────────────────────────────────

function StatChip({ label, value, Icon, to, hasError }: { label: string; value: number | string; Icon: React.ElementType; to: string; hasError?: boolean }) {
  return (
    <Link to={to} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${hasError ? 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30' : 'bg-gray-50 dark:bg-metallic-800 hover:bg-primary-50 dark:hover:bg-metallic-700'}`}>
      <Icon className={`w-4 h-4 shrink-0 ${hasError ? 'text-amber-500 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`} />
      <div className="min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</p>
        <p className={`text-sm font-semibold ${hasError ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>
          {hasError ? '—' : value}
        </p>
      </div>
    </Link>
  );
}

// ─── Vertical Activity Row ────────────────────────────────────────────────────

interface VerticalRowProps {
  icon: React.ElementType;
  label: string;
  color: string;
  chips: { label: string; value: number | string; urgent?: boolean }[];
  ctaLabel: string;
  ctaPath: string;
  active: boolean;
}

function VerticalRow({ icon: Icon, label, color, chips, ctaLabel, ctaPath, active }: VerticalRowProps) {
  if (!active) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-4 border-b border-gray-100 dark:border-gray-800 last:border-0">
      <div className="flex items-center gap-2 sm:w-44 shrink-0">
        <div className={`p-1.5 rounded-md ${color}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{label}</span>
      </div>
      <div className="flex flex-wrap gap-2 flex-1">
        {chips.map((c) => (
          <span
            key={c.label}
            className={`text-xs px-2.5 py-1 rounded-full font-medium ${
              c.urgent && Number(c.value) > 0
                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            {c.label}: <strong>{c.value}</strong>
          </span>
        ))}
      </div>
      <Link
        to={ctaPath}
        className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium flex items-center gap-1 shrink-0"
      >
        {ctaLabel} <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DashboardPage() {
  useCanonical('/dashboard');
  const { user, organizationModules } = useAuthStore();

  const hasModule = (mod: string) => organizationModules.includes(mod);

  // Map each purchasable vertical module to the vertical string stored in
  // satellite_boundaries.metadata->>'vertical' / detected_anomalies.
  const VERTICAL_MAP: Record<string, string> = {
    mvp1_agrotech:        'agrotech',
    mvp3_oil_gas:         'oil_gas',
    mvp4_infrastructure:  'infrastructure',
    mvp5_environmental:   'environmental',
    mvp6_linear_assets:   'linear_assets',
  };

  // Derive the list of vertical strings that are currently active for this org.
  const activeVerticals = Object.entries(VERTICAL_MAP)
    .filter(([mod]) => hasModule(mod))
    .map(([, v]) => v);

  // Single vertical string to pass to anomaly queries when exactly one is
  // active; undefined when multiple are active (return all) or none.
  const singleVertical = activeVerticals.length === 1 ? activeVerticals[0] : undefined;
  // Whether any anomaly data should be shown at all.
  const anyVerticalActive = activeVerticals.length > 0;

  const [errorBannerDismissed, setErrorBannerDismissed] = useState(false);

  // ── Data fetching ────────────────────────────────────────────────────────────
  const { data: missionStats, isLoading: loadingMissions, isError: missionsError } = useQuery({
    queryKey: ['dashboard-mission-stats'],
    queryFn: async () => {
      const res = await missionsApi.getAllStats();
      return res.data as {
        totalMissions: number;
        totalImages: number;
        totalAnalyses: number;
        missions: { total: number; planned: number; in_progress: number; completed: number; failed: number };
        images: number;
        analyses: number;
      };
    },
  });

  const { data: recentMissions, isLoading: loadingRecent } = useQuery({
    queryKey: ['dashboard-recent-missions'],
    queryFn: async () => {
      const res = await missionsApi.getAll({ limit: 5 });
      return res.data?.missions ?? [];
    },
  });

  const { data: anomalyStats, isLoading: loadingAnomalies, isError: anomaliesError } = useQuery({
    queryKey: ['dashboard-anomaly-stats', singleVertical, anyVerticalActive],
    enabled: anyVerticalActive,
    queryFn: async () => {
      const res = await anomalyApi.getStats(singleVertical);
      return res as {
        total: number;
        bySeverity: Record<string, number>;
        byStatus: Record<string, number>;
        recentTrend: number;
      };
    },
  });

  const { data: pendingAnomalies } = useQuery({
    queryKey: ['dashboard-pending-anomalies', singleVertical, anyVerticalActive],
    enabled: anyVerticalActive,
    queryFn: async () => {
      const res = await anomalyApi.getAnomalies({
        status: 'pending',
        ...(singleVertical ? { vertical: singleVertical } : {}),
      });
      return (res.anomalies ?? []).slice(0, 5) as any[];
    },
  });

  // O&G-specific anomaly count (oil_gas vertical only)
  const { data: ogAnomalyStats } = useQuery({
    queryKey: ['dashboard-og-anomaly-stats'],
    enabled: hasModule('mvp3_oil_gas'),
    queryFn: async () => {
      const res = await anomalyApi.getAnomalies({ status: 'pending', vertical: 'oil_gas' } as any);
      return (res.anomalies ?? []).length as number;
    },
  });

  // O&G-specific pending analysis review count
  const { data: ogReviewCount } = useQuery({
    queryKey: ['dashboard-og-review-count'],
    enabled: hasModule('mvp3_oil_gas'),
    queryFn: async () => {
      const res = await missionsApi.getAll({ mission_type: 'ogi_survey', limit: 100 } as any);
      const missions = res.data?.missions ?? [];
      return missions.filter(
        (m: any) => m.metadata?.vertical === 'oil_gas' &&
                    m.metadata?.ai_analysis_status === 'complete' &&
                    !m.metadata?.step7_status
      ).length as number;
    },
  });

  const { data: hitlStats, isLoading: loadingHITL, isError: hitlError } = useQuery({
    queryKey: ['dashboard-hitl-stats'],
    queryFn: async () => {
      const [statsRes, pendingRes] = await Promise.all([
        hitlApi.getStats().catch(() => ({ data: { totalOverrides: 0 } })),
        hitlApi.getPendingDecisions({ limit: 100 }).catch(() => ({ data: { decisions: [] } })),
      ]);
      const statsData = statsRes.data as { totalOverrides?: number } | undefined;
      const pendingData = pendingRes.data as { decisions?: any[]; total?: number } | undefined;
      return {
        totalOverrides: statsData?.totalOverrides ?? 0,
        pending_decisions: pendingData?.total ?? pendingData?.decisions?.length ?? 0,
      };
    },
  });

  const { data: boundaryStats, isError: boundaryError } = useQuery({
    queryKey: ['dashboard-boundary-stats'],
    queryFn: async () => {
      const res = await satelliteBoundariesApi.getStats().catch(() => ({}));
      return res as { total?: number; monitoring_enabled?: number };
    },
  });

  const { data: integrityStats } = useQuery({
    queryKey: ['dashboard-integrity-stats'],
    queryFn: async () => {
      const res = await integrityApi.getStats().catch(() => ({ data: {} }));
      return res.data as { total_seals?: number; tamper_alerts?: number } | undefined;
    },
  });

  const { data: recentNotifications } = useQuery({
    queryKey: ['dashboard-notifications'],
    queryFn: async () => {
      const res = await notificationsApi.getAll({ limit: 5, status: 'unread' }).catch(() => ({ data: { notifications: [] } }));
      return (res.data?.notifications ?? []) as any[];
    },
  });

  // ── Derived values ───────────────────────────────────────────────────────────

  const pendingAnomalyCount  = anomalyStats?.byStatus?.['pending'] ?? 0;
  const criticalAnomalyCount = anomalyStats?.bySeverity?.['critical'] ?? 0;
  const pendingHITL          = hitlStats?.pending_decisions ?? 0;

  // Per-vertical derived counts
  const ogPendingAnomalies = ogAnomalyStats ?? 0;
  const ogPendingReviews   = ogReviewCount ?? 0;

  // Primary routes — resolved by walking the vertical priority chain so that
  // the dashboard works correctly regardless of which verticals are active.
  // Priority: AgroTech → O&G → Infrastructure → Environmental → raw satellite fallback.
  const primaryAnomalyRoute = hasModule('mvp1_agrotech')
    ? '/agrotech/anomalies'
    : hasModule('mvp3_oil_gas')
    ? '/oil-gas/anomalies'
    : hasModule('mvp4_infrastructure')
    ? '/infrastructure/anomalies'
    : hasModule('mvp5_environmental')
    ? '/environmental/anomalies'
    : '/satellite/anomalies';

  const primaryReviewRoute = hasModule('mvp1_agrotech')
    ? '/agrotech/analysis-review'
    : hasModule('mvp3_oil_gas')
    ? '/oil-gas/analysis-review'
    : hasModule('mvp4_infrastructure')
    ? '/infrastructure/analysis-review'
    : hasModule('mvp5_environmental')
    ? '/environmental/analysis-review'
    : '/satellite/anomalies';

  const primaryBoundaryRoute = hasModule('mvp1_agrotech')
    ? '/agrotech/fields'
    : hasModule('mvp3_oil_gas')
    ? '/oil-gas/boundaries'
    : hasModule('mvp4_infrastructure')
    ? '/infrastructure/boundaries'
    : hasModule('mvp5_environmental')
    ? '/environmental/boundaries'
    : '/satellite/boundaries';

  const primarySurveyRoute = hasModule('mvp1_agrotech')
    ? '/agrotech/survey-planner'
    : hasModule('mvp3_oil_gas')
    ? '/oil-gas/survey-planner'
    : hasModule('mvp4_infrastructure')
    ? '/infrastructure/survey-planner'
    : hasModule('mvp5_environmental')
    ? '/environmental/survey-planner'
    : '/missions/create';
  const tamperAlerts = integrityStats?.tamper_alerts ?? 0;
  const unreadNotifications = recentNotifications?.length ?? 0;
  const missionsInProgress = missionStats?.missions?.in_progress ?? 0;
  const missionsPlanned = missionStats?.missions?.planned ?? 0;

  // True when at least one attention tile has a non-zero count
  const anyAttentionNeeded = pendingAnomalyCount > 0 || pendingHITL > 0 || tamperAlerts > 0 || unreadNotifications > 0;

  // True when any critical query failed — shown in the error banner
  const anyQueryFailed = missionsError || anomaliesError || hitlError || boundaryError;

  const isLoading = loadingMissions && loadingAnomalies && loadingHITL;

  if (isLoading) return <LoadingScreen message="Loading dashboard…" />;

  return (
    <div className="space-y-8">

      {/* ── API error banner ──────────────────────────────────────────────── */}
      {anyQueryFailed && !errorBannerDismissed && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-300 flex-1">
            <strong>Some data is unavailable.</strong> One or more backend services did not respond — affected counts show <strong>—</strong> instead of a number. Refresh the page or check the API status.
          </p>
          <button
            type="button"
            onClick={() => setErrorBannerDismissed(true)}
            className="text-amber-500 hover:text-amber-700 dark:hover:text-amber-300 text-lg leading-none shrink-0"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-white">
            Command Center
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Welcome back, {user?.name?.split(' ')[0] ?? 'operator'} · {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to={primarySurveyRoute}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
          >
            <Route className="w-4 h-4" /> New Survey
          </Link>
          <Link
            to="/analysis"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-metallic-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
          >
            <MessageSquare className="w-4 h-4" /> AI Analysis
          </Link>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          ZONE 1 — PRIORITY ATTENTION
          Items that require immediate human action are shown first.
          Each tile has a live count badge and links directly to the action page.
         ══════════════════════════════════════════════════════════════════════ */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-2 h-2 rounded-full ${anyAttentionNeeded ? 'bg-red-500 animate-pulse' : anyQueryFailed ? 'bg-amber-400 animate-pulse' : 'bg-gray-300 dark:bg-gray-600'}`} />
          <h2 className={`text-xs font-bold uppercase tracking-widest ${anyAttentionNeeded ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'}`}>
            {anyAttentionNeeded ? 'Requires Attention' : 'Attention Queue'}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <AttentionTile
            count={pendingAnomalyCount}
            label="Anomalies Pending Review"
            sub="Step 4 — satellite-detected, unverified"
            Icon={AlertTriangle}
            to={primaryAnomalyRoute}
            urgency={criticalAnomalyCount > 0 ? 'critical' : pendingAnomalyCount > 0 ? 'high' : 'medium'}
            loading={loadingAnomalies}
            hasError={anomaliesError}
          />
          <AttentionTile
            count={hasModule('mvp3_oil_gas') ? ogPendingReviews : pendingHITL}
            label="Analyses Awaiting Sign-off"
            sub={hasModule('mvp3_oil_gas') ? 'Step 7 — OGI safety review queue' : 'Step 7 — HITL review queue'}
            Icon={ClipboardCheck}
            to={primaryReviewRoute}
            urgency="high"
            loading={loadingHITL}
            hasError={hitlError}
          />
          <AttentionTile
            count={tamperAlerts}
            label="Data Integrity Alerts"
            sub="Tamper-detection — cryptographic seal"
            Icon={ShieldAlert}
            to="/enterprise/integrity"
            urgency="critical"
          />
          <AttentionTile
            count={unreadNotifications}
            label="Unread Notifications"
            sub="System alerts and escalations"
            Icon={Bell}
            to="/enterprise/notifications"
            urgency="medium"
          />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          ZONE 2 — OPERATIONAL PULSE
          Cross-vertical live counters: missions, images, analyses, boundaries
         ══════════════════════════════════════════════════════════════════════ */}
      <section>
        <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
          Operational Pulse
        </h2>
        <Card>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            <StatChip label="Total Missions" value={missionStats?.missions?.total ?? 0} Icon={Map} to="/missions" hasError={missionsError} />
            <StatChip label="In Progress" value={missionsInProgress} Icon={Activity} to="/missions" hasError={missionsError} />
            <StatChip label="Planned" value={missionsPlanned} Icon={Clock} to="/missions" hasError={missionsError} />
            <StatChip label="Files Ingested" value={missionStats?.totalImages ?? 0} Icon={Image} to="/missions" hasError={missionsError} />
            <StatChip label="AI Analyses" value={missionStats?.totalAnalyses ?? 0} Icon={Brain} to="/analysis" hasError={missionsError} />
            <StatChip label="Monitored Zones" value={boundaryStats?.monitoring_enabled ?? '—'} Icon={Satellite} to={primaryBoundaryRoute} hasError={boundaryError} />
          </div>

          {/* Anomaly severity breakdown */}
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex flex-wrap gap-3 items-center">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Anomaly severity:</span>
            {(['critical', 'high', 'medium', 'low'] as const).map((sev) => {
                const count = anomalyStats?.bySeverity?.[sev] ?? 0;
                return (
                  <Link
                    key={sev}
                    to={primaryAnomalyRoute}
                    className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 hover:text-primary-600"
                  >
                  <span className={`w-2 h-2 rounded-full ${SEVERITY_DOT[sev]}`} />
                  {sev.charAt(0).toUpperCase() + sev.slice(1)}: <strong>{count}</strong>
                </Link>
              );
            })}
          </div>
        </Card>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          ZONE 3 — RECENT MISSIONS
          Last 5 missions across all verticals with status badges.
         ══════════════════════════════════════════════════════════════════════ */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
            Recent Missions
          </h2>
          <Link to="/missions" className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <Card padding="none">
          {loadingRecent ? (
            <div className="p-6 text-sm text-gray-400 text-center animate-pulse">Loading missions…</div>
          ) : recentMissions && recentMissions.length > 0 ? (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {recentMissions.map((m: any) => (
                <Link
                  key={m.id}
                  to={`/missions/${m.id}`}
                  className="flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-metallic-800 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center shrink-0">
                    <Navigation className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{m.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                      {missionSubtitle(m)}
                      <span className="ml-2">{m.start_time ? new Date(m.start_time).toLocaleDateString() : m.created_at ? timeAgo(m.created_at) : ''}</span>
                    </p>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${MISSION_STATUS_STYLE[m.status] ?? 'bg-gray-100 text-gray-700'}`}>
                    {m.status}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 px-6">
              <Map className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">No missions yet</p>
              <Link
                to="/missions/create"
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
              >
                Create first mission
              </Link>
            </div>
          )}
        </Card>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          ZONE 4 — PENDING ANOMALIES LIST
          Top pending anomalies with severity indicator — direct action entry.
         ══════════════════════════════════════════════════════════════════════ */}
      {pendingAnomalies && pendingAnomalies.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Anomalies — Pending Verification
            </h2>
            <Link to={primaryAnomalyRoute} className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1">
              Review all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <Card padding="none">
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {pendingAnomalies.map((a: any) => (
                <Link
                  key={a.id}
                  to={
                    a.metadata?.vertical === 'oil_gas' ? '/oil-gas/anomalies'
                    : a.metadata?.vertical === 'infrastructure' ? '/infrastructure/anomalies'
                    : a.metadata?.vertical === 'environmental' ? '/environmental/anomalies'
                    : primaryAnomalyRoute
                  }
                  className="flex items-center gap-3 px-5 py-3.5 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors"
                >
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${SEVERITY_DOT[a.severity] ?? 'bg-gray-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {a.anomaly_type?.replace(/_/g, ' ')} — {a.boundary_name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {a.affected_area_hectares ? `${Number(a.affected_area_hectares).toFixed(1)} ha · ` : ''}
                      Detected {timeAgo(a.detection_date)}
                    </p>
                  </div>
                  <span className={`shrink-0 capitalize text-xs font-medium px-2 py-0.5 rounded-full ${
                    a.severity === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                    a.severity === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                  }`}>
                    {a.severity}
                  </span>
                </Link>
              ))}
            </div>
          </Card>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          ZONE 5 — VERTICAL ACTIVITY
          One row per active vertical with inline KPI chips.
         ══════════════════════════════════════════════════════════════════════ */}
      <section>
        <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
          Vertical Activity
        </h2>
        <Card>
          <VerticalRow
            active={hasModule('mvp1_agrotech')}
            icon={Leaf}
            color="bg-green-600"
            label="Estate Intelligence"
            chips={[
              { label: 'Anomalies pending', value: pendingAnomalyCount, urgent: pendingAnomalyCount > 0 },
              { label: 'Reviews pending', value: pendingHITL, urgent: pendingHITL > 0 },
              { label: 'Missions in-flight', value: missionsInProgress },
            ]}
            ctaLabel="Open anomalies"
            ctaPath="/agrotech/anomalies"
          />
          <VerticalRow
            active={hasModule('mvp3_oil_gas')}
            icon={Flame}
            color="bg-orange-600"
            label="Pipeline & Asset"
            chips={[
              { label: 'Anomalies pending', value: ogPendingAnomalies, urgent: ogPendingAnomalies > 0 },
              { label: 'Reviews pending',   value: ogPendingReviews,   urgent: ogPendingReviews > 0 },
              { label: 'Surveys planned',   value: missionsPlanned },
            ]}
            ctaLabel="Corridor monitor"
            ctaPath="/oil-gas/anomalies"
          />
          <VerticalRow
            active={hasModule('mvp4_infrastructure')}
            icon={Building2}
            color="bg-blue-600"
            label="Infrastructure Monitor"
            chips={[
              { label: 'Anomalies pending', value: pendingAnomalyCount, urgent: pendingAnomalyCount > 0 },
              { label: 'Surveys planned', value: missionsPlanned },
            ]}
            ctaLabel="Asset monitor"
            ctaPath="/infrastructure/anomalies"
          />
          <VerticalRow
            active={hasModule('mvp5_environmental')}
            icon={TreePine}
            color="bg-emerald-700"
            label="Environmental Monitor"
            chips={[
              { label: 'Anomalies pending', value: pendingAnomalyCount, urgent: pendingAnomalyCount > 0 },
              { label: 'Surveys planned', value: missionsPlanned },
            ]}
            ctaLabel="Zone monitor"
            ctaPath="/environmental/anomalies"
          />

          {/* Fallback when no vertical modules are active */}
          {!hasModule('mvp1_agrotech') && !hasModule('mvp3_oil_gas') && !hasModule('mvp4_infrastructure') && !hasModule('mvp5_environmental') && (
            <div className="text-center py-8 text-sm text-gray-400 dark:text-gray-500">
              No vertical modules are currently active.{' '}
              <Link to="/platform-admin/verticals" className="text-primary-600 hover:underline">
                Activate verticals
              </Link>
            </div>
          )}
        </Card>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          ZONE 6 — RECENT NOTIFICATIONS
          Unread system alerts (only shown when non-empty).
         ══════════════════════════════════════════════════════════════════════ */}
      {recentNotifications && recentNotifications.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Unread Alerts
            </h2>
            <Link to="/enterprise/notifications" className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <Card padding="none">
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {recentNotifications.slice(0, 4).map((n: any) => (
                <div key={n.id} className="flex items-start gap-3 px-5 py-3.5">
                  <Bell className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{n.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{n.message}</p>
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">{timeAgo(n.created_at)}</span>
                </div>
              ))}
            </div>
          </Card>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          ZONE 7 — QUICK ACTIONS
          Contextual shortcuts — always at bottom.
         ══════════════════════════════════════════════════════════════════════ */}
      <section>
        <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
              { icon: Route,         label: 'Plan Survey',        sub: 'Step 5',       to: primarySurveyRoute,   color: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' },
              { icon: AlertTriangle, label: 'Review Anomalies',   sub: 'Step 4',       to: primaryAnomalyRoute,  color: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20' },
              { icon: ClipboardCheck,label: 'HITL Sign-off',      sub: 'Step 7',       to: primaryReviewRoute,   color: 'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20' },
              { icon: Navigation,    label: 'Mission Control',    sub: 'All missions', to: '/missions',          color: 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20' },
              { icon: MessageSquare, label: 'AI Analysis',        sub: 'AI analysis',  to: '/analysis',          color: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20' },
              { icon: BarChart3,     label: hasModule('mvp1_agrotech') ? 'Crop Reports' : 'Reports', sub: 'Reports', to: hasModule('mvp1_agrotech') ? '/agrotech/reports' : primaryAnomalyRoute, color: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20' },
            ].map((action) => {
            const Icon = action.icon;
            return (
              <Link key={action.label} to={action.to}>
                <Card hover className="h-full flex flex-col items-center text-center py-4 px-3">
                  <div className={`p-2.5 rounded-xl mb-2 ${action.color.split(' ').slice(2).join(' ')}`}>
                    <Icon className={`w-5 h-5 ${action.color.split(' ').slice(0, 2).join(' ')}`} />
                  </div>
                  <p className="text-xs font-semibold text-gray-900 dark:text-white leading-tight">{action.label}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{action.sub}</p>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── Platform health strip ────────────────────────────────────────────── */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link to="/enterprise/integrity">
          <Card hover className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${(integrityStats?.tamper_alerts ?? 0) > 0 ? 'bg-red-500' : 'bg-green-500'}`}>
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Data Integrity</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {integrityStats?.total_seals ?? 0} seals
              </p>
              {(integrityStats?.tamper_alerts ?? 0) > 0 ? (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {integrityStats?.tamper_alerts} tamper alert{(integrityStats?.tamper_alerts ?? 0) > 1 ? 's' : ''}
                </p>
              ) : (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> All seals verified
                </p>
              )}
            </div>
          </Card>
        </Link>

        <Link to="/enterprise/audit">
          <Card hover className="flex items-center gap-3">
            <div className="bg-blue-500 p-2.5 rounded-lg">
              <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">HITL Reviews</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {hitlStats?.totalOverrides ?? 0} decisions
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {pendingHITL > 0 ? (
                  <span className="text-amber-600">{pendingHITL} pending sign-off</span>
                ) : (
                  'Queue clear'
                )}
              </p>
            </div>
          </Card>
        </Link>

        <Link to="/enterprise/notifications">
          <Card hover className="flex items-center gap-3">
            <div className="bg-purple-500 p-2.5 rounded-lg">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Anomaly Trend</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {anomalyStats?.total ?? 0} total
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {(anomalyStats?.recentTrend ?? 0) > 0
                  ? <span className="text-red-500">↑ {anomalyStats?.recentTrend} this week</span>
                  : (anomalyStats?.recentTrend ?? 0) < 0
                  ? <span className="text-green-500">↓ {Math.abs(anomalyStats?.recentTrend ?? 0)} this week</span>
                  : 'No trend data'}
              </p>
            </div>
          </Card>
        </Link>
      </section>

    </div>
  );
}

// Made with Bob
